import { useState, type ReactNode } from "react";
import { DataTable, type Column } from "./DataTable.tsx";
import { Modal } from "../molecules/Modal.tsx";
import { TablePager } from "../molecules/TablePager.tsx";
import { ConfirmDialog } from "../molecules/ConfirmDialog.tsx";
import { LoadingState, ErrorState, EmptyState } from "../molecules/StateBlock.tsx";
import { Button } from "../atoms/Button.tsx";
import type { UseResource } from "../../lib/useResource.ts";
import { useToast } from "../../lib/toast.tsx";
import { errorMessage } from "../../lib/errors.ts";

type Base = { id: string; published: boolean; sortOrder: number };

interface FormProps<T> {
  initial: T | null;
  onDone: () => void;
}

interface Props<T extends Base, TInput, TUpdate> {
  intro: string;
  resource: UseResource<T, TInput, TUpdate>;
  columns: Column<T>[];
  emptyText: string;
  newLabel: string;
  formTitle: (editing: boolean) => string;
  renderForm: (props: FormProps<T>) => ReactNode;
  itemLabel: (item: T) => string;
}

export function CrudPage<T extends Base, TInput, TUpdate>({
  intro,
  resource,
  columns,
  emptyText,
  newLabel,
  formTitle,
  renderForm,
  itemLabel,
}: Props<T, TInput, TUpdate>) {
  const toast = useToast();
  const [editing, setEditing] = useState<T | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<T | null>(null);

  const closeForm = () => {
    setEditing(null);
    setCreating(false);
  };

  const togglePublish = async (item: T) => {
    try {
      await resource.update(item.id, { published: !item.published } as unknown as TUpdate);
      toast.success(item.published ? "Unpublished" : "Published");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const reorder = async (ordered: T[]) => {
    try {
      await resource.reorder(ordered);
      toast.success("Order updated");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await resource.remove(deleting.id);
      toast.success("Deleted");
      setDeleting(null);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const formOpen = creating || editing !== null;

  return (
    <>
      <div className="page-actions">
        <p>{intro}</p>
        <Button variant="primary" onClick={() => setCreating(true)}>
          + {newLabel}
        </Button>
      </div>

      {resource.loading ? (
        <LoadingState />
      ) : resource.error ? (
        <ErrorState message={resource.error} onRetry={resource.refresh} />
      ) : resource.items.length === 0 ? (
        <EmptyState>{emptyText}</EmptyState>
      ) : (
        <>
          <DataTable
            items={resource.items}
            columns={columns}
            onEdit={setEditing}
            onDelete={setDeleting}
            onTogglePublish={togglePublish}
            onReorder={reorder}
          />
          <TablePager
            page={resource.page}
            totalPages={resource.totalPages}
            total={resource.total}
            hasMore={resource.hasMore}
            onPage={resource.setPage}
          />
        </>
      )}

      {formOpen && (
        <Modal title={formTitle(editing !== null)} onClose={closeForm}>
          {renderForm({ initial: editing, onDone: closeForm })}
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete item"
          message={`Delete "${itemLabel(deleting)}"? This cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  );
}
